import { useEffect, useState } from "react";
import { Brand, ThemeToggle } from "@championcare/ui";
import { Link, useRouter } from "../router";
import { patientSignInUrl } from "../config";

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

/** Scrolls to a landing-page section by id, navigating home first if we're on another route. */
function useScrollToSection() {
  const { path, navigate } = useRouter();
  return (id: string) => {
    const go = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (path !== "/") {
      navigate("/");
      setTimeout(go, 60);
    } else {
      go();
    }
  };
}

/** Dark editorial masthead (always dark, mirrors `.topbar` in championcare-design). */
export function Nav() {
  const { navigate } = useRouter();
  const scrollTo = useScrollToSection();
  const [menuOpen, setMenuOpen] = useState(false);

  // Drive the drawer + toggle-icon swap off `body.menu-open` (mirrors the reference CSS).
  useEffect(() => {
    document.body.classList.toggle("menu-open", menuOpen);
    return () => document.body.classList.remove("menu-open");
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <header className="topbar">
        <div className="topbar-inner">
          <Link to="/" className="brand" aria-label="Champion Care home">
            <Brand onDark size={22} />
          </Link>

          <nav className="topbar-nav" aria-label="Primary">
            <button className="topbar-link" onClick={() => scrollTo("how")}>How it works</button>
            <button className="topbar-link" onClick={() => scrollTo("doctors")}>Doctors</button>
            <button className="topbar-link" onClick={() => scrollTo("plans")}>Plans</button>
            <button className="topbar-link" onClick={() => scrollTo("faq")}>FAQ</button>
          </nav>

          <div className="topbar-actions">
            <div className="topbar-desktop-actions">
              <ThemeToggle />
              <a className="btn btn-text btn-sm topbar-signin" href={patientSignInUrl()}>
                Sign in
              </a>
              <button className="btn btn-primary btn-sm" onClick={() => navigate("/signup")}>
                Get started
                <ArrowIcon />
              </button>
            </div>
            <button
              className="mobile-toggle"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              <svg className="burger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="7" x2="21" y2="7" /><line x1="3" y1="17" x2="21" y2="17" /></svg>
              <svg className="x" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="6" y1="18" x2="18" y2="6" /></svg>
            </button>
          </div>
        </div>
      </header>

      <div className="mobile-drawer" id="mobile-drawer">
        <button className="mobile-drawer-link" onClick={() => { closeMenu(); scrollTo("how"); }}>How it works <ChevronIcon /></button>
        <button className="mobile-drawer-link" onClick={() => { closeMenu(); scrollTo("doctors"); }}>Doctors <ChevronIcon /></button>
        <button className="mobile-drawer-link" onClick={() => { closeMenu(); scrollTo("plans"); }}>Plans <ChevronIcon /></button>
        <button className="mobile-drawer-link" onClick={() => { closeMenu(); scrollTo("faq"); }}>FAQ <ChevronIcon /></button>
        <div className="mobile-drawer-actions">
          <a className="btn btn-ghost" href={patientSignInUrl()} onClick={closeMenu}>Sign in</a>
          <button className="btn btn-primary" onClick={() => { closeMenu(); navigate("/signup"); }}>
            Get started
            <ArrowIcon />
          </button>
          <div className="mobile-drawer-theme">
            <span>Theme</span>
            <ThemeToggle />
          </div>
        </div>
      </div>
    </>
  );
}
